import { useMutation, useQuery } from '@tanstack/react-query';
import { OSM_API, OsmHttpError } from '@/api/osmClient';
import type { GeocodeResult } from '@/types/osm';

// Public Nominatim — used as a fallback when the local proxy returns 503
// (Nominatim wasn't deployed to the VPS to save the 14 GB import). Their
// usage policy asks for a real User-Agent / contact and ≤1 req/s; the
// browser sets its own UA and the SPA only fires on user submit, which
// satisfies both. SA addresses geocode correctly via the public service.
const PUBLIC_NOMINATIM = 'https://nominatim.openstreetmap.org';

async function fetchJson<T>(url: string): Promise<T> {
  const r = await fetch(url, { headers: { Accept: 'application/json' } });
  if (!r.ok) {
    const body = await r.text().catch(() => '');
    throw new OsmHttpError(r.status, body);
  }
  return (await r.json()) as T;
}

async function geocodeForward(q: string): Promise<GeocodeResult[]> {
  const qs = new URLSearchParams({
    q,
    format: 'json',
    limit: '8',
    addressdetails: '1',
  }).toString();
  try {
    return await fetchJson<GeocodeResult[]>(`${OSM_API}/geocode/forward?${qs}`);
  } catch (err) {
    if (err instanceof OsmHttpError && err.status === 503) {
      // Fall back to public Nominatim. Add a SA viewbox bias so addresses
      // resolve to the right country when ambiguous.
      const fallbackQs = new URLSearchParams({
        q,
        format: 'json',
        limit: '8',
        addressdetails: '1',
        countrycodes: 'za,ls,sz,bw,na,zw,mz',
      }).toString();
      return await fetchJson<GeocodeResult[]>(`${PUBLIC_NOMINATIM}/search?${fallbackQs}`);
    }
    throw err;
  }
}

async function geocodeReverse(lat: number, lon: number): Promise<GeocodeResult> {
  const qs = new URLSearchParams({
    lat: String(lat),
    lon: String(lon),
    format: 'json',
  }).toString();
  try {
    return await fetchJson<GeocodeResult>(`${OSM_API}/geocode/reverse?${qs}`);
  } catch (err) {
    if (err instanceof OsmHttpError && err.status === 503) {
      return await fetchJson<GeocodeResult>(`${PUBLIC_NOMINATIM}/reverse?${qs}`);
    }
    throw err;
  }
}

export function useOsmForwardGeocode() {
  return useMutation<GeocodeResult[], Error, string>({
    mutationFn: geocodeForward,
  });
}

export function useOsmReverseGeocode(lat: number | null, lon: number | null, enabled = true) {
  return useQuery<GeocodeResult>({
    queryKey: ['osm', 'reverse', lat, lon],
    enabled: enabled && lat != null && lon != null,
    queryFn: () => geocodeReverse(lat as number, lon as number),
    retry: false,
  });
}
